import { CheckCircle2, Wrench, XCircle } from 'lucide-react-native';
import { Box, Text } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { ProspectSearchToolCall } from '../types';
import { redactUserFacingText } from '../utils/safety';
import { getToolFriendlyLabel } from '../utils/toolLabels';

interface ToolCardProps {
  readonly toolCall: ProspectSearchToolCall;
}

export function ToolCard({ toolCall }: ToolCardProps) {
  const failed = Boolean(toolCall.error);
  const statusLabel = failed ? 'error' : 'completed';
  const label = getToolFriendlyLabel(toolCall.name);
  const StatusIcon = failed ? XCircle : CheckCircle2;
  const statusColor = failed ? colors.status.error.main : colors.status.success.main;

  return (
    <Box
      testID={`tool-card-${toolCall.id}`}
      accessibilityRole="text"
      accessibilityLabel={`${label}: ${statusLabel}`}
      flexDirection="row"
      alignItems="center"
      gap="xs"
      style={[styles.card, failed && styles.cardError]}
    >
      <Box style={styles.iconBadge} backgroundColor={colors.background.tertiary}>
        <Wrench size={12} color={colors.text.tertiary} strokeWidth={1.8} />
      </Box>
      <Text variant="caption" color={colors.text.secondary} style={styles.label} numberOfLines={1}>
        {label}
      </Text>
      <StatusIcon size={12} color={statusColor} strokeWidth={2} />
      {failed && toolCall.error && (
        <Text
          variant="caption"
          color={colors.status.error.main}
          numberOfLines={1}
          style={styles.errorText}
        >
          {redactUserFacingText(toolCall.error)}
        </Text>
      )}
    </Box>
  );
}

const styles = {
  card: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    borderRadius: tokens.radius.md,
    backgroundColor: colors.background.card,
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginTop: 6,
    alignSelf: 'flex-start' as const,
    maxWidth: '100%' as const,
  },
  cardError: {
    borderColor: colors.status.error.border,
  },
  iconBadge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  label: {
    fontWeight: '500' as const,
    flexShrink: 1,
  },
  errorText: {
    flexShrink: 1,
  },
};
